import { getDb } from './db'
import { compressImageIfNeeded } from '@/utils/imageCompress'
import { authStore } from '@/store/auth'
import { orgStore } from '@/store/org'
import type { CreateExpensePayload } from '@/features/finance/api'
import type { ExpenseRequest, RequestHistoryItem } from '@/types'
import type { ExpenseDocument, OfflineReceiptMeta } from './schema'
import {
  projectOfflineExpenseToHistory,
  projectOfflineExpenseToRequest,
} from './expenseProjection'

export const EXPENSES_CHANGED_EVENT = 'granula:expenses-changed'

function operationId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export function notifyExpensesChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EXPENSES_CHANGED_EVENT))
}

export function offlineExpenseToHistory(
  doc: ExpenseDocument,
  orgCurrency?: string,
): RequestHistoryItem {
  const user = authStore.getState().user
  return projectOfflineExpenseToHistory(
    doc,
    user?.display_name || user?.full_name || user?.username || 'You',
    orgCurrency,
  )
}

export function offlineExpenseToRequest(
  doc: ExpenseDocument,
  orgCurrency?: string,
): ExpenseRequest {
  return projectOfflineExpenseToRequest(doc, authStore.getState().user, orgCurrency)
}

export async function listOfflineExpenseDocuments(): Promise<ExpenseDocument[]> {
  const userId = authStore.getState().user?.id
  if (!userId) return []
  const orgId = orgStore.getState().org?.id
  const db = await getDb()
  const documents = await db.expenses.find().exec()
  return documents
    .map((doc) => doc.toJSON() as ExpenseDocument)
    .filter((doc) => doc.user_id === userId && (orgId == null || doc.org_id === orgId))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
}

export async function queueOfflineExpense(
  payload: CreateExpensePayload,
  files: File[] = [],
): Promise<ExpenseRequest> {
  const userId = authStore.getState().user?.id
  if (!userId) throw new Error('You must be signed in to queue an expense')
  const orgId = orgStore.getState().org?.id ?? 0
  const createOpId = operationId('expense')
  const rxdbId = `expense_${createOpId}`
  const now = new Date().toISOString()
  const preparedFiles: File[] = []

  for (const file of files) {
    preparedFiles.push(await compressImageIfNeeded(file, true))
  }

  const receipts: OfflineReceiptMeta[] = preparedFiles.map((file, index) => ({
    attachment_id: `receipt_${index}_${createOpId}`,
    op_id: `${createOpId}:receipt:${index}`,
    file_name: file.name,
    content_type: file.type,
    size_bytes: file.size,
    synced: false,
  }))

  const db = await getDb()
  const document = await db.expenses.insert({
    rxdb_id: rxdbId,
    local_id: -Date.now(),
    server_id: null,
    org_id: orgId,
    user_id: userId,
    payload: JSON.stringify(payload),
    receipts,
    sync_status: 'pending_sync',
    last_error: null,
    created_at: now,
    updated_at: now,
    dirty: true,
  })

  if (preparedFiles.length) {
    await document.putAttachments(preparedFiles.map((file, index) => ({
      id: receipts[index].attachment_id,
      type: file.type,
      data: file,
    })))
  }

  await db.outbox.insert({
    op_id: createOpId,
    op_type: 'create_expense',
    rxdb_id: rxdbId,
    server_id: null,
    payload: JSON.stringify(payload),
    attempts: 0,
    last_attempt_at: null,
    last_error: null,
    created_at: now,
  })
  notifyExpensesChanged()
  return offlineExpenseToRequest(document.toJSON() as ExpenseDocument)
}
